import React, { useRef, useEffect, useCallback } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import type { Content } from "../types";
import { useLanguage } from "../../../context/LanguageContext";

interface Props {
  content: Content;
  onChange: (c: Content) => void;
  onSave: () => void;
  onClose: () => void;
  saving?: boolean;
  uploadProgress?: number;
  uploadError?: string | null;
}

const FC = "w-full px-4 py-3 border border-gray-200 rounded-xl text-sm text-gray-900 outline-none transition-all focus:border-[#b2d12e] focus:ring-4 focus:ring-[#b2d12e]/20";
const FC_SM = "w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-900 outline-none transition-all focus:border-[#b2d12e] focus:ring-4 focus:ring-[#b2d12e]/20";

const ContentEditModal: React.FC<Props> = ({ content, onChange, onSave, onClose, saving = false, uploadProgress = 0, uploadError = null }) => {
  const { t } = useLanguage();
  const set = (field: keyof Content, value: any) => onChange({ ...content, [field]: value });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFileName, setSelectedFileName] = React.useState<string>("");
  const [selectedFileSize, setSelectedFileSize] = React.useState<string>("");
  const [videoPreview, setVideoPreview] = React.useState<string>("");
  const [isDragging, setIsDragging] = React.useState(false);
  const [descLen, setDescLen] = React.useState((content.description ?? "").length);
  const [confirmClose, setConfirmClose] = React.useState(false);
  const [videoError, setVideoError] = React.useState<string | null>(null);

  // Revoke old object URL on cleanup
  useEffect(() => {
    return () => {
      if (videoPreview) URL.revokeObjectURL(videoPreview);
    };
  }, [videoPreview]);

  const handleClose = useCallback(() => {
    setConfirmClose(true);
  }, []);

  const formatSize = (bytes: number) => {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const processFile = (file: File) => {
    if (!file.type.startsWith("video/")) return;
    if (file.size > 50 * 1024 * 1024) {
      setVideoError(t("admin.contentEditModal.videoTooLarge"));
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setVideoError(null);
    if (videoPreview) URL.revokeObjectURL(videoPreview);
    const previewUrl = URL.createObjectURL(file);
    setVideoPreview(previewUrl);
    setSelectedFileName(file.name);
    setSelectedFileSize(formatSize(file.size));
    // Single onChange call — two separate set() calls use the same stale content
    // snapshot and the second would overwrite the first, losing videoFile.
    onChange({ ...content, videoFile: file, video: "" });
  };

  const handleVideoFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  const removeVideo = () => {
    if (videoPreview) URL.revokeObjectURL(videoPreview);
    setVideoPreview("");
    setSelectedFileName("");
    setSelectedFileSize("");
    setVideoError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    onChange({ ...content, videoFile: null, video: "" });
  };

  const hasUploadedVideo = !!content.videoFile || !!videoPreview || !!content.video;
  const hasExternalUrl = !!content.video_url;
  const bothFilled = hasUploadedVideo && hasExternalUrl;
  const canSave = !!content.title && !!content.category;

  const categoryValue = typeof content.category === "string"
    ? content.category
    : (content.category as any)?.name ?? "";

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-start z-[100] p-4 overflow-y-auto"
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl my-8">

        {/* Header */}
        <div className="sticky top-0 flex items-center justify-between px-6 py-5 border-b border-gray-100 bg-white rounded-t-2xl z-10">
          <div>
            <h2 className="text-lg font-bold text-gray-900">
              {content.id ? t("admin.contentEditModal.editTitle") : t("admin.contentEditModal.createTitle")}
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {content.id ? t("admin.contentEditModal.idLabel", { id: content.id }) : t("admin.contentEditModal.fillDetails")}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <XMarkIcon className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6 space-y-5">

          {/* Title */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
              {t("admin.contentEditModal.titleLabel")} <span className="text-red-400">*</span>
            </label>
            <input
              value={content.title}
              onChange={(e) => set("title", e.target.value)}
              placeholder={t("admin.contentEditModal.titlePlaceholder")}
              className={FC}
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
              {t("admin.contentEditModal.categoryLabel")} <span className="text-red-400">*</span>
            </label>
            {/* Note: category can be stored as either string or object, but we always save as string */}
            <input
              value={categoryValue}
              onChange={(e) => set("category", e.target.value)}
              placeholder={t("admin.contentEditModal.categoryPlaceholder")}
              className={FC}
            />
          </div>

          {/* Description */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-sm font-semibold text-gray-700">{t("admin.contentEditModal.descriptionLabel")}</label>
              <span className={`text-xs ${descLen > 500 ? "text-red-400" : "text-gray-400"}`}>
                {t("admin.contentEditModal.charsCount", { count: descLen })}
              </span>
            </div>
            <textarea
              value={content.description ?? ""}
              onChange={(e) => { set("description", e.target.value); setDescLen(e.target.value.length); }}
              rows={4}
              placeholder={t("admin.contentEditModal.descriptionPlaceholder")}
              className={`${FC} resize-none`}
            />
          </div>

          {/* Video Sources */}
          <div className="border border-gray-200 rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-[#b2d12e]" />
                <h3 className="text-sm font-semibold text-gray-700">{t("admin.contentEditModal.videoSources")}</h3>
              </div>
              {hasUploadedVideo && (
                <button
                  onClick={removeVideo}
                  className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700 transition-colors font-medium"
                >
                  <XMarkIcon className="w-3.5 h-3.5" />
                  {t("admin.contentEditModal.remove")}
                </button>
              )}
            </div>

            <div className="p-4 space-y-4">

              {/* Conflict warning */}
              {bothFilled && (
                <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-xl">
                  <span className="text-yellow-500 text-sm mt-0.5">⚠</span>
                  <p className="text-xs text-yellow-700">
                    {t("admin.contentEditModal.bothVideoWarning")}
                  </p>
                </div>
              )}

              {/* Upload zone or preview */}
              {!hasUploadedVideo ? (
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    {t("admin.contentEditModal.uploadFileLabel")}
                  </label>
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={handleDrop}
                    className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${isDragging
                      ? "border-[#b2d12e] bg-[#b2d12e]/5"
                      : "border-gray-200 hover:border-[#b2d12e]/50 hover:bg-gray-50"
                      }`}
                  >
                    <div className="text-2xl mb-2">🎬</div>
                    <p className="text-sm font-medium text-gray-700">
                      {isDragging ? t("admin.contentEditModal.dropItHere") : t("admin.contentEditModal.clickOrDrag")}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">{t("admin.contentEditModal.videoFormatsHint")}</p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="video/mp4,video/quicktime,video/x-msvideo,video/x-matroska,video/*"
                      onChange={handleVideoFileSelect}
                      className="hidden"
                    />
                  </div>
                  {videoError && (
                    <div className="mt-2 flex items-center gap-1.5 p-2.5 bg-red-50 border border-red-200 rounded-xl">
                      <span className="text-red-500 text-sm shrink-0">⚠</span>
                      <p className="text-xs text-red-700 font-medium">{videoError}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    {content.videoFile ? t("admin.contentEditModal.newUpload") : t("admin.contentEditModal.currentVideo")}
                  </label>
                  {selectedFileName && (
                    <div className="flex items-center justify-between px-3 py-2 bg-[#b2d12e]/10 border border-[#b2d12e]/20 rounded-xl mb-2">
                      <span className="text-xs text-gray-700 font-medium truncate">{selectedFileName}</span>
                      {selectedFileSize && (
                        <span className="text-xs text-gray-400 ml-2 shrink-0">{selectedFileSize}</span>
                      )}
                    </div>
                  )}
                  <video
                    key={videoPreview || content.video}
                    src={videoPreview || content.video}
                    controls
                    className="w-full rounded-xl max-h-48 bg-black"
                  />
                </div>
              )}

              {/* External URL */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  {t("admin.contentEditModal.externalUrlLabel")}
                </label>
                <input
                  value={content.video_url ?? ""}
                  onChange={(e) => set("video_url", e.target.value)}
                  placeholder={t("admin.contentEditModal.videoUrlPlaceholder")}
                  className={FC_SM}
                />
              </div>
            </div>
          </div>

          {/* Metrics */}
          <div className="border border-gray-200 rounded-2xl overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 border-b border-gray-200">
              <div className="w-2 h-2 rounded-full bg-green-400" />
              <h3 className="text-sm font-semibold text-gray-700">{t("admin.contentEditModal.metrics")}</h3>
            </div>
            <div className="p-4 grid grid-cols-3 gap-3">
              {(["views", "likes", "shares"] as const).map((key) => (
                <div key={key}>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                    {t(`admin.contentEditModal.metric${key.charAt(0).toUpperCase()}${key.slice(1)}`)}
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={content.metrics?.[key] ?? 0}
                    onChange={(e) =>
                      set("metrics", {
                        ...(content.metrics ?? { views: 0, likes: 0, shares: 0 }),
                        [key]: Math.max(0, Number(e.target.value)),
                      })
                    }
                    className={FC_SM}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Upload progress / error */}
        {(saving && content.videoFile) && (
          <div className="px-6 pb-2">
            {uploadProgress < 100 ? (
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-semibold text-gray-600">{t("admin.contentEditModal.uploadingVideo")}</span>
                  <span className="text-xs font-bold text-[#b2d12e]">{uploadProgress}%</span>
                </div>
                <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#b2d12e] rounded-full transition-all duration-200"
                    style={{ width: `${Math.max(uploadProgress, 2)}%` }}
                  />
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-xl">
                <span className="text-green-500 text-base">✓</span>
                <span className="text-xs font-semibold text-green-700">{t("admin.contentEditModal.uploadComplete")}</span>
              </div>
            )}
          </div>
        )}
        {uploadError && !saving && (
          <div className="px-6 pb-2">
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl">
              <span className="text-red-500 text-sm shrink-0">⚠</span>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-red-700">{t("admin.contentEditModal.uploadFailedLabel")}</p>
                <p className="text-xs text-red-600 mt-0.5 break-words">{uploadError}</p>
              </div>
              <button
                onClick={onSave}
                className="shrink-0 text-xs font-bold text-red-700 hover:text-red-900 underline"
              >
                {t("common.retry")}
              </button>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="sticky bottom-0 flex justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-white rounded-b-2xl">
          <button
            onClick={handleClose}
            className="px-5 py-2.5 text-gray-600 font-semibold rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors text-sm"
          >
            {t("common.cancel")}
          </button>
          <button
            onClick={onSave}
            disabled={!canSave || saving}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#b2d12e] text-gray-900 font-semibold rounded-xl hover:bg-[#9dba27] transition-colors text-sm disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saving && (
              <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
            )}
            {saving ? t("common.saving") : t("admin.contentEditModal.saveChanges")}
          </button>
        </div>
      </div>

      {/* Discard confirmation */}
      {confirmClose && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm mx-4">
            <h3 className="text-base font-bold text-gray-900 mb-2">{t("admin.contentEditModal.discardChangesTitle")}</h3>
            <p className="text-sm text-gray-500 mb-5">{t("admin.contentEditModal.discardChangesText")}</p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmClose(false)}
                className="flex-1 px-4 py-2 border border-gray-200 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
              >
                {t("admin.contentEditModal.keepEditing")}
              </button>
              <button
                onClick={() => { setConfirmClose(false); onClose(); }}
                className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-xl text-sm font-semibold transition-colors"
              >
                {t("admin.contentEditModal.discard")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentEditModal;